import {
  Appointment,
  GenerateRoomModel,
  UnavailableSlotModel,
} from '@sr/data-access'
import { convertToDate } from '@shared/utils'
import { flow, types } from 'mobx-state-tree'
import {
  APPOINTMENT_TYPES,
  APPOINTMENT_TYPES_COLORS,
  APPOINTMENT_TYPES_TRANSLATION,
  BaseRouteState,
  LEGEND_COLORS,
  LEGEND_TEXT_COLOR,
  LEGEND_TYPES,
  SocialReferent,
} from '@shared/data-access'
import { actions } from '../common'

export const CalendarAppointments = BaseRouteState.named('CalendarAppointments')
  .props({
    userId: types.maybeNull(types.string),
    appointments: types.maybeNull(
      types.array(types.safeReference(Appointment), {}),
    ),
    unavailableSlots: types.maybeNull(
      types.array(types.safeReference(UnavailableSlotModel), {}),
    ),
    user: types.maybeNull(types.reference(SocialReferent)),
    generatedRoom: types.maybeNull(types.reference(GenerateRoomModel)),
  })
  .views((self) => ({
    get appointmentsFormatted() {
      const slots = [
        ...(self.appointments ? self.appointments : []),
        ...(self.unavailableSlots
          ? self.formatUnavailableSlots(self.unavailableSlots)
          : []),
      ]
      const allEvents = slots?.map((slot) => {
        let {
          id,
          startDate,
          endDate,
          effectiveStart,
          effectiveEnd,
          appointmentType,
          isInitialReport,
          isInitialAnalysis,
          isPhoneCall,
          isOnline,
          isFaceToFace,
          policy,
          meetingUrl,
        } = slot

        let personalizedColor = null
        if (isInitialReport && isFaceToFace) {
          appointmentType = APPOINTMENT_TYPES.INITIAL_REPORT_FACE_TO_FACE
        }
        if (isInitialAnalysis && isPhoneCall) {
          personalizedColor = LEGEND_COLORS[LEGEND_TYPES.initialAnalysisCall]
        }
        if (isInitialAnalysis && isFaceToFace) {
          personalizedColor =
            LEGEND_COLORS[LEGEND_TYPES.initialAnalysisFaceToFace]
        }
        if (isInitialAnalysis && isOnline) {
          personalizedColor = LEGEND_COLORS[LEGEND_TYPES.initialAnalysisOnline]
        }
        if (
          appointmentType === APPOINTMENT_TYPES.FOLLOW_UP_ANALYSIS &&
          isPhoneCall
        ) {
          personalizedColor = LEGEND_COLORS[LEGEND_TYPES.yearlyFollowUpCall]
        }

        const originalStart = convertToDate(startDate)
        const originalEnd = convertToDate(endDate)
        const effectiveStartTime = effectiveStart ? convertToDate(effectiveStart) : null
        const effectiveEndTime = effectiveEnd ? convertToDate(effectiveEnd) : null
        
        const events = []
        
        const mainEvent = {
          ...slot,
          id,
          start: originalStart,
          end: originalEnd,
          appointmentType: {
            reference: appointmentType,
            text: APPOINTMENT_TYPES_TRANSLATION[appointmentType],
            color: personalizedColor || APPOINTMENT_TYPES_COLORS[appointmentType],
            textColor: LEGEND_TEXT_COLOR[appointmentType] || '',
          },
          policy,
          meetingUrl,
          isMainEvent: true,
        }
        events.push(mainEvent)
        
        if (effectiveStartTime && effectiveStartTime.getTime() !== originalStart.getTime()) {
          events.push({
            ...slot,
            id: `${id}-travel-before`,
            start: effectiveStartTime,
            end: originalStart,
            appointmentType: {
              reference: 'TRAVEL_TIME',
              text: 'Tiempo de traslado',
              color: 'appointment-travel-time',
              textColor: 'text-white',
            },
            policy: null,
            meetingUrl: null,
            isTravelTime: true,
            travelType: 'before',
            originalAppointmentStart: startDate,
            originalAppointmentEnd: endDate,
            originalAppointmentId: id,
            currentEffectiveStart: effectiveStart,
            currentEffectiveEnd: effectiveEnd,
          })
        }
        
        if (effectiveEndTime && effectiveEndTime.getTime() !== originalEnd.getTime()) {
          events.push({
            ...slot,
            id: `${id}-travel-after`,
            start: originalEnd,
            end: effectiveEndTime,
            appointmentType: {
              reference: 'TRAVEL_TIME',
              text: 'Tiempo de traslado',
              color: 'appointment-travel-time',
              textColor: 'text-white',
            },
            policy: null,
            meetingUrl: null,
            isTravelTime: true,
            travelType: 'after',
            originalAppointmentStart: startDate,
            originalAppointmentEnd: endDate,
            originalAppointmentId: id,
            currentEffectiveStart: effectiveStart,
            currentEffectiveEnd: effectiveEnd,
          })
        }
        
        return events
      })
      
      return allEvents?.flat() || []
    },
  }))
  .actions((self) => ({
    loadDependencies: flow(function* loadDependencies(startDate, endDate) {
      self.setLoading('CalendarAppointments')
      try {
        yield self.loadUserInfo()
        yield self.loadAppointments({ startDate, endDate, id: self.userId })
        yield self.loadUnavailableSlots(startDate, endDate, self.userId)
      }
      catch (error) {
        console.error(error)
      }
      finally {
        self.removeLoading('CalendarAppointments')
      }
    }),
    setUserId(id) {
      self.userId = id
    },
    ...actions(self),
  }))
